/**
 * どの画面からも出うる共通メッセージ (権限不足・セッション切れ・タイムアウト等) の表示時翻訳。
 *
 * これらの文言は通信層 (`api/client.ts`) やトースト Provider が **日本語で作る**。作る側を
 * 翻訳すると、文字列で判定している既存の分岐 (例: `err.message.includes('セッション')`) を
 * 壊すため、作る側は変えずに **表示する直前** (ToastContainer) でだけ英語へ置き換える。
 * 登録された日本語の文言テンプレート (`common.knownMessages` など、名前空間ごとに登録) と
 * 完全一致したものだけを訳し、
 * それ以外 (画面ごとの文言・サーバーの文言) はそのまま出す。
 */
import './namespaces/common';
import { namespaceDictionary, registeredKnownMessages } from './registry';
import { lookupMessage, translate } from './translate';
import { DEFAULT_LOCALE, type Locale } from './types';

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 文言テンプレート (`{name}` を含む) に完全一致すれば差し込み値を返す。一致しなければ null。
 * 例: 'リクエストがタイムアウトしました ({seconds}秒)' と 'リクエストがタイムアウトしました (30秒)'
 * → { seconds: '30' }
 */
export function matchMessageTemplate(
  template: string,
  message: string,
): Record<string, string> | null {
  const names: string[] = [];
  const pattern = template
    .split(/(\{\w+\})/)
    .map((part) => {
      const placeholder = /^\{(\w+)\}$/.exec(part);
      if (placeholder) {
        names.push(placeholder[1]);
        return '(.+?)';
      }
      return escapeRegExp(part);
    })
    .join('');
  const match = new RegExp(`^${pattern}$`).exec(message);
  if (!match) return null;
  const params: Record<string, string> = {};
  names.forEach((name, index) => {
    params[name] = match[index + 1];
  });
  return params;
}

/**
 * 共通メッセージなら表示言語へ訳し、そうでなければそのまま返す。
 * 対象は `defineNamespace(..., { knownMessages })` で登録されたキー (登録順に照合する)。
 * `common` の分 (権限不足・セッション切れ等) はこのファイルが必ず登録させる。
 */
export function translateKnownMessage(locale: Locale, message: string): string {
  if (locale === DEFAULT_LOCALE) return message;
  for (const { namespace, key } of registeredKnownMessages()) {
    const ja = namespaceDictionary(namespace, DEFAULT_LOCALE);
    const template = ja ? lookupMessage(ja, key) : undefined;
    if (template === undefined) continue;
    const params = matchMessageTemplate(template, message);
    if (params) return translate(locale, `${namespace}.${key}`, params);
  }
  return message;
}
